import {
  Input,
  Button,
  Typography,
  Textarea,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import Select from 'react-select';
import { addBusinessType, resetStatus } from "@/store/slices/businessTypeSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

const notBlankRegex = /\S/;
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = (type, t) => Yup.object({
  name: Yup.string()
    .trim()
    .required(t("nameValidationRequired", { type: type }))
    .min(3, t("nameValidationMin"))
    .max(50, t("nameValidationMax"))
    .matches(notBlankRegex, t("nameValidationWhitespace"))
    .matches(noHTMLRegex, t("nameValidationHtml"))
    .matches(noCodeRegex, t("nameValidationCode"))
    .matches(noConsecutiveSpecialChars, t("nameValidationConsecutiveSpecial"))
    .matches(noLeadingTrailingPunct, t("nameValidationPunctuation")),

  description: Yup.string()
    .trim()
    .required(t("descriptionValidationRequired"))
    .min(10, t("descriptionValidationMin"))
    .max(500, t("descriptionValidationMax"))
    .matches(notBlankRegex, t("descriptionValidationWhitespace"))
    .matches(noHTMLRegex, t("descriptionValidationHtml"))
    .matches(noCodeRegex, t("descriptionValidationCode"))
    .matches(noConsecutiveSpecialChars, t("descriptionValidationConsecutiveSpecial"))
    .matches(noLeadingTrailingPunct, t("descriptionValidationPunctuation")),
});

export function AddBusinessType({ type = 'business', isEdit = false, isView = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const title = type.charAt(0).toUpperCase() + type.slice(1);
  const { t } = useTranslation("business-type");

  const statusOptions = [{
    label: t('activeLabel'),
    value: 'active'
  },
  {
    label: t('inactiveLabel'),
    value: 'inactive'
  }];

  const selectedBusinessType = useSelector((state) => state.businessType.selected);
  const { loading } = useSelector((state) => state.businessType);

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema(type, t)),
    defaultValues: {
      name: '',
      description: '',
      type: type,
      status: "active",
    }
  });


  useEffect(() => {
    if (isEdit) {
      if (selectedBusinessType && String(selectedBusinessType.id) === String(id)) {
        reset({ ...selectedBusinessType });
      }
    } else {
      reset({ name: '', description: '', type: type, status: "active" });
    }
  }, [isEdit, id, selectedBusinessType, dispatch]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(addBusinessType(data)).unwrap();
      if (result.status) {
        Toaster.success(result.message || (isEdit ? t("updateSuccessMessage") : t("addSuccessMessage")));
        dispatch(resetStatus());
        reset();
        navigate(`/dashboard/${type}-types`);
      } else {
        Toaster.error(result.message || t("somethingWentWrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("addFailedMessage"));
      dispatch(resetStatus());
    }
  };

  const getTitle = () => {
    if (type === 'business') {
      return isView ? t("viewBusinessTypeTitle") : isEdit ? t("editBusinessTypeTitle") : t("addBusinessTypetitle");
    } else {
      return isView ? t("viewVendorTypeTitle") : isEdit ? t("editVendorTypeTitle") : t("addVendorTypeTitle");
    }
  }

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {getTitle()}
              </Typography>
            </div>
            {!isView &&
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/${type}-types`)}
                  className="rounded-lg"
                >
                  {t("cancelButton")}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white">
                  {loading ? t("savingButton") : isEdit ? t("updateButton") : t("saveButton")}
                </Button>
              </div>
            }
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/dashboard/${type}-types`} className="flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("backButton")}
            </Link>
          </div>
        </div>
        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2 border-b border-blue-gray-200">
              <Typography variant="h4" color="blue-gray">
                {t("basicDetails")}
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {type === 'business' ? t("businessTypeLabel") : t("vendorTypeLabel")} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={type === 'business' ? t("enterBusinessTypePlaceholder") : t("enterVendorTypePlaceholder")}
                  {...register('name')}
                  error={Boolean(errors.name)}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t("statusLabel")} <span className="text-red-500">*</span>
                </Typography>
                <Select
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t('selectStatusPlaceholder')}
                  onChange={(status) => {
                    setValue('status', status?.value, { shouldValidate: true });
                  }}
                  value={statusOptions?.find(opt => opt.value === watch('status')) || null}
                  options={statusOptions}
                  error={Boolean(errors.status)}
                  isClearable
                  isDisabled={isView}
                />
                {errors.status && (
                  <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
                )}
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t("descriptionLabel")} <span className="text-red-500">*</span>
                </Typography>
                <Textarea
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t('enterDescriptionPlaceholder')}
                  {...register('description')}
                  error={Boolean(errors.description)}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

export default AddBusinessType;